import { TrustBadgesDS } from '@src/components/blocks/trust-badges-ds'
import type { TrustBadgeRow } from '@src/components/blocks/trust-badges-ds'
import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import { resolveSectionBackground } from '../widget-background'
import { toRichText } from '../widget-utils'

import type { TrustBadgesWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

// Matches legacy's root testid verbatim so QA can select the widget root the same way in either mode.
// @i18n-ignore
const TRUST_BADGES_WIDGET_TEST_ID = 'trust-badges-widget'
// @i18n-ignore
const TRUST_BADGE_KEY_FALLBACK = 'trust-badge'

/**
 * DS branch of the PL `TRUST_BADGES` widget: normalizes each proto badge into a typed `TrustBadgeRow`
 * (rich text → Contentful `Document`) and delegates rendering to `TrustBadgesDS`. `classNames` are
 * cast until the proto carries them (parity with the legacy widget + FEATURE_CARDS).
 */
export function TrustBadgesDSWidget(props: PLTypedWidgetProps<TrustBadgesWidgetData>) {
  const data = props.data.trustBadgesData
  const items = data?.items ?? []
  const classNames = (data as { classNames?: string[] } | undefined)?.classNames

  // Mobile carousel gate, identical to `TrustBadgesWidgetLegacy`: the non-PL source section
  // (`TEXT_BLOCK_WITH_ICON_ROW`) renders one badge per slide with dots when its `layoutType` is
  // CAROUSEL. The proto doesn't carry `layoutType` yet (pending BE contract, like `classNames`), so
  // it's read via cast and a missing value defaults to CAROUSEL — every known TRUST_BADGES page today
  // is a carousel section, and the cast honours an explicit DEFAULT the moment the BE sends one.
  // Gated on the device (not CSS) so the badges render ONCE: the non-PL icon row can afford CSS-hidden
  // twin trees because its two trees hold different per-device CMS content, but here they'd be
  // identical — duplicating every badge's copy for crawlers and screen readers.
  const deviceInfo = useDeviceInfo()
  const layoutType = (data as { layoutType?: string } | undefined)?.layoutType
  const isMobileCarousel = deviceInfo.deviceType === DEVICE_TYPES.MOBILE && layoutType !== LAYOUT_TYPES.DEFAULT

  const rows: TrustBadgeRow[] = items.map((item, index) => {
    const document = toRichText(item.subTitle)?.raw
    return {
      // Title alone isn't guaranteed unique (or may be blank) → suffix the index for a stable key.
      key: `${item.title || TRUST_BADGE_KEY_FALLBACK}-${index}`,
      icon: item.icon,
      title: item.title,
      subTitle: document?.content.length ? document : undefined,
    }
  })

  return (
    <TrustBadgesDS
      heading={data?.heading}
      rows={rows}
      backgroundColor={resolveSectionBackground(classNames)}
      testId={TRUST_BADGES_WIDGET_TEST_ID}
      isMobileCarousel={isMobileCarousel}
    />
  )
}
